/**
 * Agent Ops — the runtime's human control plane. Budgets (creating the first
 * row ACTIVATES enforcement), model routes (data, not code — rule #4), the
 * guarded kill switch, invocation/run telemetry.
 */
import { useState } from "react";
import { Bars, Chip, Empty, ErrorNote, Panel, Readout, statusTone, useApi } from "../components/ui.js";
import { api } from "../lib/api.js";
import { failRateInWindow, fmtDateTime, int, n, pct, usd } from "../lib/format.js";
import type { BudgetRow, CostDay, InvocationRow, ModelRow, RouteRow, RunRow } from "../lib/types.js";

export function AgentOps() {
  const ks = useApi<{ paused: boolean }>("/api/killswitch");
  const budgets = useApi<{ budgets: BudgetRow[] }>("/api/budgets");
  const routes = useApi<{ routes: RouteRow[] }>("/api/routes");
  const models = useApi<{ models: ModelRow[] }>("/api/models");
  const invocations = useApi<{ invocations: InvocationRow[] }>("/api/agents/invocations?limit=25");
  const runs = useApi<{ runs: RunRow[] }>("/api/agents/runs?limit=15");
  const cost = useApi<{ costByDay: CostDay[] }>("/api/agents/cost-by-day");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ scopeKind: "global", scopeKey: "", period: "monthly", budgetUsd: "250", hardLimit: true });

  async function toggleKillSwitch() {
    if (!ks.data) return;
    const next = !ks.data.paused;
    const verb = next ? "PAUSE ALL OUTBOUND" : "RESUME outbound sending";
    if (!window.confirm(`${verb}? Takes effect on the next runner tick.`)) return;
    setBusy(true);
    try { await api.post("/api/killswitch", { paused: next }); ks.reload(); } finally { setBusy(false); }
  }

  async function createBudget() {
    setBusy(true);
    try {
      await api.post("/api/budgets", {
        scopeKind: form.scopeKind,
        scopeKey: form.scopeKey || undefined,
        period: form.period,
        budgetUsd: Number(form.budgetUsd),
        hardLimit: form.hardLimit,
      });
      budgets.reload();
    } finally { setBusy(false); }
  }

  async function toggleRoute(r: RouteRow) {
    setBusy(true);
    try { await api.patch(`/api/routes/${r.id}`, { isActive: !r.is_active }); routes.reload(); } finally { setBusy(false); }
  }

  const inv = invocations.data?.invocations ?? [];
  const fails = inv.filter((i) => i.status === "failed").length;
  // Windowed failure rate — a single old bad day must not paint the panel red.
  const health7 = failRateInWindow(cost.data?.costByDay ?? [], 7);
  const health30 = failRateInWindow(cost.data?.costByDay ?? [], 30);
  const failAlarm = health7.invocations >= 10 && health7.rate > 0.1;

  return (
    <>
      <div className="page-head"><h1>Agent Ops</h1><span className="stamp">runtime control plane</span></div>

      <div className="grid cols-2" style={{ marginBottom: 14 }}>
        <Panel title="Global kill switch" alarm={ks.data?.paused}>
          <div className="killswitch">
            <div className={`ks-toggle ${ks.data?.paused ? "on" : ""}`} onClick={busy ? undefined : toggleKillSwitch} role="switch" aria-checked={ks.data?.paused ?? false}>
              <div className="lever">{ks.data?.paused ? "HALT" : "RUN"}</div>
            </div>
            <div className="ks-status">
              {ks.data?.paused
                ? <span className="on">⏸ all outbound paused — due enrollments wait, nothing is lost</span>
                : <span className="off">● outbound running — flip to halt every send on the next tick</span>}
            </div>
          </div>
        </Panel>

        <Panel title="Spend by day">
          {cost.error ? <ErrorNote error={cost.error} /> : (
            <Bars
              values={dailyTotals(cost.data?.costByDay ?? [])}
              color="var(--amber)"
            />
          )}
        </Panel>
      </div>

      <div className="grid cols-2" style={{ marginBottom: 14 }}>
        <Panel title="Budgets — caps runtime.invoke() enforces" alarm={budgets.data?.budgets.length === 0}>
          {budgets.data?.budgets.length === 0 ? (
            <div className="tag" style={{ color: "#ff8392", marginBottom: 10 }}>
              zero rows = enforcement OFF. creating the first cap activates it.
            </div>
          ) : null}
          {(budgets.data?.budgets ?? []).length > 0 ? (
            <table className="data" style={{ marginBottom: 12 }}>
              <thead><tr><th>scope</th><th>period</th><th>cap</th><th>spent</th><th>hard</th></tr></thead>
              <tbody>
                {(budgets.data?.budgets ?? []).map((b) => (
                  <tr key={b.id}>
                    <td>{b.scope_kind}{b.scope_key ? <span className="dim"> · {b.scope_key}</span> : ""}</td>
                    <td className="dim">{b.period}</td>
                    <td className="num">{usd(b.budget_usd)}</td>
                    <td className="num" style={{ color: n(b.spent_usd) / Math.max(n(b.budget_usd), 1) > n(b.alert_threshold) ? "var(--amber)" : undefined }}>{usd(b.spent_usd)}</td>
                    <td>{b.hard_limit ? <Chip tone="red">hard</Chip> : <Chip>soft</Chip>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          <div className="row">
            <label className="fld"><span>scope</span>
              <select value={form.scopeKind} onChange={(e) => setForm({ ...form, scopeKind: e.target.value })}>
                {["global", "vendor", "agent_role", "task_class"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
            <label className="fld"><span>scope key</span>
              <input style={{ width: 110 }} placeholder="(global: none)" value={form.scopeKey} onChange={(e) => setForm({ ...form, scopeKey: e.target.value })} />
            </label>
            <label className="fld"><span>period</span>
              <select value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })}>
                {["hourly", "daily", "weekly", "monthly"].map((p) => <option key={p}>{p}</option>)}
              </select>
            </label>
            <label className="fld"><span>cap usd</span>
              <input style={{ width: 80 }} value={form.budgetUsd} onChange={(e) => setForm({ ...form, budgetUsd: e.target.value })} />
            </label>
            <label className="fld"><span>hard</span>
              <select value={String(form.hardLimit)} onChange={(e) => setForm({ ...form, hardLimit: e.target.value === "true" })}>
                <option value="true">block</option><option value="false">alert only</option>
              </select>
            </label>
            <button className="btn primary sm" disabled={busy} onClick={createBudget} style={{ alignSelf: "end" }}>Set cap</button>
          </div>
        </Panel>

        <Panel title="Model routes — selection is data (rule #4)">
          {routes.error ? <ErrorNote error={routes.error} /> : (
            <table className="data">
              <thead><tr><th>role</th><th>task</th><th>→ model</th><th>pri</th><th>active</th></tr></thead>
              <tbody>
                {(routes.data?.routes ?? []).map((r) => (
                  <tr key={r.id}>
                    <td>{r.agent_role_key || <span className="faint">any</span>}</td>
                    <td className="dim">{r.task_class || "any"}</td>
                    <td className="mono">{r.routes_to}</td>
                    <td className="num">{r.priority}</td>
                    <td>
                      <button className={`btn sm ${r.is_active ? "ghost-green" : "danger"}`} disabled={busy} onClick={() => toggleRoute(r)}>
                        {r.is_active ? "on" : "off"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="tag" style={{ marginTop: 8 }}>
            models available: {(models.data?.models ?? []).map((m) => m.key).join(" · ") || "—"}
          </div>
        </Panel>
      </div>

      <div className="grid cols-2">
        <Panel title={`Recent invocations ${fails > 0 ? `· ${fails} failed in view` : ""}`} alarm={failAlarm}>
          {invocations.error ? <ErrorNote error={invocations.error} /> : inv.length === 0 ? <Empty>no invocations</Empty> : (
            <table className="data">
              <thead><tr><th>model</th><th>purpose</th><th>status</th><th>cost</th><th>ms</th><th>when</th></tr></thead>
              <tbody>
                {inv.map((i) => (
                  <tr key={i.id} title={i.error_detail ?? undefined}>
                    <td className="mono">{i.model ?? "—"}</td>
                    <td className="dim">{i.purpose}{i.task_class ? ` · ${i.task_class}` : ""}</td>
                    <td><Chip tone={statusTone(i.status)}>{i.status}</Chip></td>
                    <td className="num">{usd(i.total_cost_usd)}</td>
                    <td className="num dim">{int(i.latency_ms)}</td>
                    <td className="num dim">{fmtDateTime(i.started_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title="Agent runs">
          {(runs.data?.runs ?? []).length === 0 ? <Empty>no runs</Empty> : (
            <table className="data">
              <thead><tr><th>agent</th><th>model</th><th>status</th><th>step</th><th>created</th></tr></thead>
              <tbody>
                {(runs.data?.runs ?? []).map((r) => (
                  <tr key={r.id} title={r.error ?? undefined}>
                    <td>{r.agent_name}</td>
                    <td className="mono dim">{r.model}</td>
                    <td><Chip tone={statusTone(r.status)}>{r.status}</Chip></td>
                    <td className="num dim">{r.current_step}</td>
                    <td className="num dim">{fmtDateTime(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="tag" style={{ marginTop: 10 }}>
            failure rate — 7d {pct(health7.rate)} ({int(health7.failed)}/{int(health7.invocations)}) · 30d {pct(health30.rate)} ({int(health30.failed)}/{int(health30.invocations)})
          </div>
        </Panel>
      </div>
    </>
  );
}

function dailyTotals(rows: CostDay[]): number[] {
  const byDay = new Map<string, number>();
  for (const r of rows) byDay.set(r.day, (byDay.get(r.day) ?? 0) + n(r.total_cost_usd));
  return [...byDay.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1)).map(([, v]) => v);
}
